/**
 * EF-PLATFORM-USER-MANAGEMENT-RBAC-V1: Tenant Management Page
 * 
 * Superadmin-only page for managing tenant companies and their users.
 * Features:
 * - List all tenants with status and user counts
 * - Create new tenant with initial admin user
 * - View/edit tenant details
 * - Manage tenant users (create, reset password, enable/disable)
 * - Role assignment for tenant users
 * - Disable tenant accounts
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Building2, Users, Plus, Settings, Key, UserX, UserCheck, 
  RefreshCw, ChevronRight, Search, AlertTriangle, Shield, Edit
} from 'lucide-react';

// Status badge colors
const statusColors = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  trial: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  suspended: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  inactive: 'bg-red-500/20 text-red-400 border-red-500/30',
};

// Role display names and colors
const TENANT_ROLES = [
  { value: 'tenant_admin', label: 'Tenant Admin', color: 'bg-purple-500/20 text-purple-400' },
  { value: 'operations_manager', label: 'Operations Manager', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'dispatcher', label: 'Dispatcher', color: 'bg-cyan-500/20 text-cyan-400' },
  { value: 'accounts', label: 'Accounts', color: 'bg-amber-500/20 text-amber-400' },
  { value: 'client', label: 'Client', color: 'bg-gray-500/20 text-gray-400' },
  { value: 'crew', label: 'Crew', color: 'bg-green-500/20 text-green-400' },
];

const getRoleDisplay = (role) => {
  const roleInfo = TENANT_ROLES.find(r => r.value === role);
  return roleInfo || { value: role, label: role, color: 'bg-gray-500/20 text-gray-400' };
};

export default function TenantManagementPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [createForm, setCreateForm] = useState({
    company_id: '',
    company_name: '',
    contact_email: '',
    contact_name: '',
    contact_phone: '',
    admin_email: '',
    admin_password: '',
    admin_name: '',
  });

  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    full_name: '',
    mobile_number: '',
    role: 'tenant_admin',
  });

  const [newPassword, setNewPassword] = useState('');
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  // EVENTSFLO-TENANT-MANAGEMENT-EDIT-TENANT-ADMIN-AND-PASSWORD-RESET-V1
  const [showEditTenantDialog, setShowEditTenantDialog] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);

  // Check if user is superadmin
  useEffect(() => {
    if (user?.role !== 'superadmin') {
      toast.error('Access denied. Superadmin privileges required.');
      window.location.href = '/dashboard';
    }
  }, [user]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, tenantsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/tenants', { params: { status: statusFilter !== 'all' ? statusFilter : undefined } }),
      ]);
      setStats(statsRes.data);
      setTenants(tenantsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load tenant data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch tenant users
  const fetchTenantUsers = async (tenantId) => {
    try {
      const response = await api.get(`/admin/tenants/${tenantId}/users`);
      setTenantUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch tenant users:', error);
      toast.error('Failed to load tenant users');
    }
  };

  // Create tenant
  const handleCreateTenant = async () => {
    try {
      await api.post('/admin/tenants', createForm);
      toast.success(`Tenant "${createForm.company_name}" created successfully`);
      setShowCreateDialog(false);
      setCreateForm({
        company_id: '',
        company_name: '',
        contact_email: '',
        contact_name: '',
        contact_phone: '',
        admin_email: '',
        admin_password: '',
        admin_name: '',
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    }
  };

  // Create tenant user
  const handleCreateUser = async () => {
    if (!selectedTenant) return;
    try {
      await api.post(`/admin/tenants/${selectedTenant.company_id}/users`, {
        email: userForm.email,
        password: userForm.password,
        full_name: userForm.full_name,
        mobile_number: userForm.mobile_number,
        role: userForm.role,
      });
      toast.success(`User "${userForm.email}" created successfully`);
      setShowUserDialog(false);
      setUserForm({ email: '', password: '', full_name: '', mobile_number: '', role: 'tenant_admin' });
      fetchTenantUsers(selectedTenant.company_id);
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  // Update user role
  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      // EVENTSFLO-TENANT-MANAGEMENT-EDIT-TENANT-ADMIN-AND-PASSWORD-RESET-V1:
      // include email (SuperAdmin can edit the tenant admin email address).
      // Only send email if it actually changed to avoid unnecessary writes.
      const payload = {
        role: editingUser.role,
        full_name: editingUser.full_name,
        mobile_number: editingUser.mobile_number,
      };
      const original = tenantUsers.find((u) => u.id === editingUser.id);
      if (
        editingUser.email &&
        editingUser.email.trim().toLowerCase() !== (original?.email || '').toLowerCase()
      ) {
        payload.email = editingUser.email.trim().toLowerCase();
      }
      await api.patch(`/admin/users/${editingUser.id}`, payload);
      toast.success(`User "${editingUser.email}" updated successfully`);
      setShowEditUserDialog(false);
      setEditingUser(null);
      if (selectedTenant) {
        fetchTenantUsers(selectedTenant.company_id);
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // EVENTSFLO-TENANT-MANAGEMENT-EDIT-TENANT-ADMIN-AND-PASSWORD-RESET-V1
  // Update tenant details (superadmin only).
  const handleUpdateTenant = async () => {
    if (!editingTenant) return;
    try {
      const payload = {
        company_name: editingTenant.company_name,
        contact_email: editingTenant.contact_email,
        contact_name: editingTenant.contact_name || null,
        contact_phone: editingTenant.contact_phone || null,
        notes: editingTenant.notes || null,
      };
      const updated = await api.patch(
        `/admin/tenants/${editingTenant.company_id}`,
        payload
      );
      toast.success(`Tenant "${updated.data.company_name}" updated`);
      setShowEditTenantDialog(false);
      setEditingTenant(null);
      // Refresh both the list and the currently-open detail panel.
      await fetchData();
      setSelectedTenant((prev) =>
        prev && prev.company_id === updated.data.company_id
          ? { ...prev, ...updated.data }
          : prev
      );
    } catch (error) {
      console.error('Failed to update tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    }
  };

  // EVENTSFLO-TENANT-MANAGEMENT-EDIT-TENANT-ADMIN-AND-PASSWORD-RESET-V1
  // Client-side strong-temporary-password generator. 16 chars with
  // upper/lower/digit/symbol coverage — never leaves the browser except
  // via HTTPS to the SuperAdmin reset endpoint.
  const generateStrongTempPassword = () => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnpqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%^&*_-+=';
    const all = upper + lower + digits + symbols;
    const pick = (set) => set[Math.floor(Math.random() * set.length)];
    // Ensure at least one of each class then fill to 16 chars.
    let out = [pick(upper), pick(lower), pick(digits), pick(symbols)];
    for (let i = out.length; i < 16; i++) out.push(pick(all));
    // Fisher-Yates shuffle
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out.join('');
  };

  // Reset password
  const handleResetPassword = async () => {
    if (!selectedUser) return;
    // EVENTSFLO-TENANT-MANAGEMENT-EDIT-TENANT-ADMIN-AND-PASSWORD-RESET-V1:
    // require typed confirmation to prevent accidental resets.
    if (resetConfirmText.trim() !== 'RESET PASSWORD') {
      toast.error('Type RESET PASSWORD to confirm');
      return;
    }
    try {
      await api.patch(`/admin/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword,
      });
      toast.success(
        `Password reset for ${selectedUser.email}. Share the new password with the user through a secure channel — it will not be shown again.`
      );
      setShowPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
      setResetConfirmText('');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Toggle user status
  const handleToggleUserStatus = async (userId, currentActive) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, {
        active: !currentActive,
      });
      toast.success(`User ${currentActive ? 'disabled' : 'enabled'} successfully`);
      if (selectedTenant) {
        fetchTenantUsers(selectedTenant.company_id);
      }
    } catch (error) {
      console.error('Failed to update user status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  // Disable tenant
  const handleDisableTenant = async (tenant) => {
    if (!window.confirm(`Are you sure you want to disable "${tenant.company_name}"? This will also disable all users for this tenant.`)) {
      return;
    }
    try {
      await api.delete(`/admin/tenants/${tenant.company_id}`);
      toast.success(`Tenant "${tenant.company_name}" has been disabled`);
      fetchData();
      setSelectedTenant(null);
    } catch (error) {
      console.error('Failed to disable tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to disable tenant');
    }
  };

  // Filter tenants
  const filteredTenants = tenants.filter(tenant => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        tenant.company_name.toLowerCase().includes(query) ||
        tenant.company_id.toLowerCase().includes(query) ||
        tenant.contact_email.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Generate company_id from company name
  const generateCompanyId = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 50);
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white">Access Denied</h2>
          <p className="text-gray-400 mt-2">Superadmin privileges required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tenant-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Tenant Management</h1>
          <p className="text-gray-400 text-sm mt-1">Manage tenant companies and their administrators</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-tenant-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Tenant
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card border-white/5">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Total Tenants</CardTitle>
              <Building2 className="h-4 w-4 text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{stats.tenants.total}</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-white/5">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Active Tenants</CardTitle>
              <Building2 className="h-4 w-4 text-green-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{stats.tenants.by_status.active || 0}</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-white/5">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Total Users</CardTitle>
              <Users className="h-4 w-4 text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{stats.users.total}</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-white/5">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Superadmins</CardTitle>
              <Key className="h-4 w-4 text-yellow-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{stats.users.superadmins}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenant List */}
        <div className="lg:col-span-2">
          <Card className="bg-card border-white/5">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-white">Tenants</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search tenants..."
                      className="pl-9 w-64 bg-background"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTenants.map((tenant) => (
                    <TableRow 
                      key={tenant.id} 
                      className={`cursor-pointer ${selectedTenant?.id === tenant.id ? 'bg-blue-500/10' : ''}`}
                      onClick={() => {
                        setSelectedTenant(tenant);
                        fetchTenantUsers(tenant.company_id);
                      }}
                      data-testid={`tenant-row-${tenant.company_id}`}
                    >
                      <TableCell>
                        <div>
                          <div className="font-medium text-white">{tenant.company_name}</div>
                          <div className="text-xs text-gray-500">{tenant.company_id}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-400">{tenant.contact_email}</div>
                        {tenant.contact_name && (
                          <div className="text-xs text-gray-500">{tenant.contact_name}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-gray-400">
                          {tenant.user_count} users
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[tenant.status]}>
                          {tenant.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTenants.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                        No tenants found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Tenant Detail Panel */}
        <div className="lg:col-span-1">
          {selectedTenant ? (
            <Card className="bg-card border-white/5">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center justify-between">
                  <span>{selectedTenant.company_name}</span>
                  <Badge className={statusColors[selectedTenant.status]}>
                    {selectedTenant.status}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="details">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger value="users">Users ({tenantUsers.length})</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="details" className="space-y-4 mt-4">
                    <div>
                      <Label className="text-gray-400 text-xs">Company ID</Label>
                      <div className="text-white font-mono text-sm">{selectedTenant.company_id}</div>
                    </div>
                    <div>
                      <Label className="text-gray-400 text-xs">Contact Email</Label>
                      <div className="text-white text-sm">{selectedTenant.contact_email}</div>
                    </div>
                    {selectedTenant.contact_name && (
                      <div>
                        <Label className="text-gray-400 text-xs">Contact Name</Label>
                        <div className="text-white text-sm">{selectedTenant.contact_name}</div>
                      </div>
                    )}
                    {selectedTenant.contact_phone && (
                      <div>
                        <Label className="text-gray-400 text-xs">Phone</Label>
                        <div className="text-white text-sm">{selectedTenant.contact_phone}</div>
                      </div>
                    )}
                    {selectedTenant.notes && (
                      <div>
                        <Label className="text-gray-400 text-xs">Notes</Label>
                        <div className="text-white text-sm">{selectedTenant.notes}</div>
                      </div>
                    )}
                    <div className="pt-4 border-t border-white/5">
                      <Label className="text-gray-400 text-xs">Created</Label>
                      <div className="text-white text-sm">
                        {new Date(selectedTenant.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    
                    {selectedTenant.status !== 'inactive' && (
                      <div className="mt-4 flex flex-col gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            setEditingTenant({ ...selectedTenant });
                            setShowEditTenantDialog(true);
                          }}
                          data-testid="edit-tenant-btn"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Tenant Details
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="w-full"
                          onClick={() => handleDisableTenant(selectedTenant)}
                          data-testid="disable-tenant-btn"
                        >
                          <UserX className="h-4 w-4 mr-2" />
                          Disable Tenant
                        </Button>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="users" className="mt-4">
                    <div className="flex justify-end mb-3">
                      <Button 
                        size="sm" 
                        onClick={() => setShowUserDialog(true)}
                        disabled={selectedTenant.status === 'inactive'}
                        data-testid="add-user-btn"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add User
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {tenantUsers.map((u) => {
                        const roleDisplay = getRoleDisplay(u.role);
                        return (
                        <div 
                          key={u.id} 
                          className="p-3 rounded-lg bg-background border border-white/5"
                          data-testid={`user-${u.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex-1">
                              <div className="text-white text-sm font-medium">{u.email}</div>
                              {(u.full_name || u.name) && <div className="text-gray-500 text-xs">{u.full_name || u.name}</div>}
                              {u.mobile_number && <div className="text-gray-500 text-xs">{u.mobile_number}</div>}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={roleDisplay.color}>
                                <Shield className="h-3 w-3 mr-1" />
                                {roleDisplay.label}
                              </Badge>
                              <Badge className={u.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                                {u.active ? 'Active' : 'Disabled'}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-xs"
                              onClick={() => {
                                setEditingUser({ ...u, role: u.role });
                                setShowEditUserDialog(true);
                              }}
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-xs"
                              onClick={() => {
                                setSelectedUser(u);
                                setShowPasswordDialog(true);
                              }}
                            >
                              <Key className="h-3 w-3 mr-1" />
                              Password
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => handleToggleUserStatus(u.id, u.active)}
                            >
                              {u.active ? (
                                <>
                                  <UserX className="h-3 w-3 mr-1" />
                                  Disable
                                </>
                              ) : (
                                <>
                                  <UserCheck className="h-3 w-3 mr-1" />
                                  Enable
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                        );
                      })}
                      {tenantUsers.length === 0 && (
                        <div className="text-center text-gray-500 py-4">
                          No users found
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-white/5">
              <CardContent className="flex items-center justify-center py-16">
                <div className="text-center text-gray-500">
                  <Settings className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Select a tenant to view details</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create Tenant Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
            <DialogDescription>
              Set up a new tenant company with an initial admin user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Company Name *</Label>
                <Input
                  value={createForm.company_name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setCreateForm({ 
                      ...createForm, 
                      company_name: name,
                      company_id: createForm.company_id || generateCompanyId(name),
                    });
                  }}
                  placeholder="Acme Events Ltd"
                  data-testid="company-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Company ID *</Label>
                <Input
                  value={createForm.company_id}
                  onChange={(e) => setCreateForm({ ...createForm, company_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  placeholder="acme_events"
                  className="font-mono"
                  data-testid="company-id-input"
                />
                <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, underscores only</p>
              </div>
              <div className="col-span-2">
                <Label>Contact Email *</Label>
                <Input
                  type="email"
                  value={createForm.contact_email}
                  onChange={(e) => setCreateForm({ ...createForm, contact_email: e.target.value })}
                  placeholder="contact@company.com"
                />
              </div>
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={createForm.contact_name}
                  onChange={(e) => setCreateForm({ ...createForm, contact_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input
                  value={createForm.contact_phone}
                  onChange={(e) => setCreateForm({ ...createForm, contact_phone: e.target.value })}
                  placeholder="+44 7700 900000"
                />
              </div>
            </div>
            
            <div className="border-t border-white/10 pt-4">
              <h4 className="font-medium text-white mb-3">Initial Admin User</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Admin Email *</Label>
                  <Input
                    type="email"
                    value={createForm.admin_email}
                    onChange={(e) => setCreateForm({ ...createForm, admin_email: e.target.value })}
                    placeholder="admin@company.com"
                    data-testid="admin-email-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Admin Password *</Label>
                  <Input
                    type="password"
                    value={createForm.admin_password}
                    onChange={(e) => setCreateForm({ ...createForm, admin_password: e.target.value })}
                    placeholder="Min 8 characters"
                    data-testid="admin-password-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Admin Name</Label>
                  <Input
                    value={createForm.admin_name}
                    onChange={(e) => setCreateForm({ ...createForm, admin_name: e.target.value })}
                    placeholder="John Smith"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateTenant}
              disabled={!createForm.company_id || !createForm.company_name || !createForm.contact_email || !createForm.admin_email || !createForm.admin_password}
              data-testid="create-tenant-submit"
            >
              Create Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add User to {selectedTenant?.company_name}</DialogTitle>
            <DialogDescription>
              Create a new user for this company with a specific role.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="user@company.com"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                placeholder="Min 8 characters"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label>Role *</Label>
              <Select
                value={userForm.role}
                onValueChange={(value) => setUserForm({ ...userForm, role: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {TENANT_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={userForm.full_name}
                  onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label>Mobile Number</Label>
                <Input
                  value={userForm.mobile_number}
                  onChange={(e) => setUserForm({ ...userForm, mobile_number: e.target.value })}
                  placeholder="+33612345678"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateUser}
              disabled={!userForm.email || !userForm.password || userForm.password.length < 8}
              data-testid="create-user-submit"
            >
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details and role for {editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, email: e.target.value })
                  }
                  placeholder="user@company.com"
                  data-testid="edit-user-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  SuperAdmin can change the tenant user email. Must be unique.
                </p>
              </div>
              <div>
                <Label>Role *</Label>
                <Select
                  value={editingUser.role}
                  onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                >
                  <SelectTrigger data-testid="edit-user-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {TENANT_ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={editingUser.full_name || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label>Mobile Number</Label>
                  <Input
                    value={editingUser.mobile_number || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, mobile_number: e.target.value })}
                    placeholder="+33612345678"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUserDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleUpdateUser}
              data-testid="update-user-submit"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(open) => {
        setShowPasswordDialog(open);
        if (!open) {
          setNewPassword('');
          setResetConfirmText('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new temporary password for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-md border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-xs text-yellow-300">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">Existing passwords cannot be viewed.</div>
                  <div>
                    Setting a new password replaces the current one. Share the
                    new password with the user via a secure channel — it will
                    not be shown again after this dialog closes.
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>New Temporary Password *</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => setNewPassword(generateStrongTempPassword())}
                  data-testid="generate-password-btn"
                >
                  Generate Strong Password
                </Button>
              </div>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
                data-testid="reset-password-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Displayed here once so you can copy it. It will not be
                retrievable after this dialog closes.
              </p>
            </div>
            <div>
              <Label>Type <span className="font-mono text-red-400">RESET PASSWORD</span> to confirm</Label>
              <Input
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="RESET PASSWORD"
                data-testid="reset-password-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleResetPassword}
              disabled={
                !newPassword ||
                newPassword.length < 8 ||
                resetConfirmText.trim() !== 'RESET PASSWORD'
              }
              data-testid="reset-password-submit"
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EVENTSFLO-TENANT-MANAGEMENT-EDIT-TENANT-ADMIN-AND-PASSWORD-RESET-V1
          Edit Tenant Dialog */}
      <Dialog
        open={showEditTenantDialog}
        onOpenChange={(open) => {
          setShowEditTenantDialog(open);
          if (!open) setEditingTenant(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Tenant Details</DialogTitle>
            <DialogDescription>
              Update the tenant company profile for {editingTenant?.company_name}.
            </DialogDescription>
          </DialogHeader>
          {editingTenant && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-400 text-xs">Company ID (read-only)</Label>
                <Input value={editingTenant.company_id} disabled className="font-mono bg-gray-800/50" />
              </div>
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={editingTenant.company_name || ''}
                  onChange={(e) =>
                    setEditingTenant({ ...editingTenant, company_name: e.target.value })
                  }
                  data-testid="edit-tenant-name-input"
                />
              </div>
              <div>
                <Label>Contact Email *</Label>
                <Input
                  type="email"
                  value={editingTenant.contact_email || ''}
                  onChange={(e) =>
                    setEditingTenant({ ...editingTenant, contact_email: e.target.value })
                  }
                  data-testid="edit-tenant-email-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={editingTenant.contact_name || ''}
                    onChange={(e) =>
                      setEditingTenant({ ...editingTenant, contact_name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={editingTenant.contact_phone || ''}
                    onChange={(e) =>
                      setEditingTenant({ ...editingTenant, contact_phone: e.target.value })
                    }
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  value={editingTenant.notes || ''}
                  onChange={(e) =>
                    setEditingTenant({ ...editingTenant, notes: e.target.value })
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTenantDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateTenant}
              disabled={
                !editingTenant?.company_name ||
                !editingTenant?.contact_email
              }
              data-testid="edit-tenant-submit"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
